import type { MouseEvent, MouseEventHandler } from 'react'
import React from 'react'

import Head from 'next/head'

import type { VALID_IMAGE_LAYOUTS, IMAGE_LOADING } from './constants'
import { LAYOUT_RESPONSIVE, LAYOUT_COVER } from './constants'
import { ImageBase } from './styles'

interface IImageProps {
  alt: string
  src: string
  layout?: (typeof VALID_IMAGE_LAYOUTS)[number]
  onClick?: MouseEventHandler
  width?: string | number
  height?: string | number
  preload?: boolean
  loading?: (typeof IMAGE_LOADING)[number]
  aspectRatio?: string
  style?: React.CSSProperties
  className?: string
  dataTestId?: string
  srcSet?: string[]
  sizes?: string[]
}

const Image = (props: IImageProps) => {
  const {
    alt,
    src,
    width,
    height,
    layout = LAYOUT_COVER,
    onClick,
    preload = false,
    loading = 'eager',
    aspectRatio = '',
    style = {},
    className,
    dataTestId,
    srcSet = [],
    sizes = [],
  } = props

  const imageDimensions = layout === LAYOUT_RESPONSIVE ? undefined : { width, height }
  const imageSrcSet = srcSet.length > 0 ? `${srcSet.join(', ')}` : undefined
  const imageSizes = sizes.length > 0 ? `${sizes.join(', ')}` : undefined

  function handleClick(event: MouseEvent<HTMLDivElement>) {
    if (onClick) onClick(event)
  }

  const linkProps = {
    as: 'image',
    href: src,
    ...(imageSrcSet && { imagesrcset: imageSrcSet }),
    ...(imageSizes && { imagesizes: imageSizes }),
    rel: 'preload',
  }

  return (
    <ImageBase
      className={className}
      $layout={layout}
      onClick={handleClick}
      $aspectRatio={aspectRatio}
      data-testid={dataTestId}
    >
      {preload && (
        <Head>
          <link {...linkProps} />
        </Head>
      )}
      <img
        src={src}
        alt={alt}
        {...imageDimensions}
        srcSet={imageSrcSet}
        sizes={imageSizes}
        {...(loading === 'eager' ? {} : { loading })}
        style={style}
      />
    </ImageBase>
  )
}

export default Image
